import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, Package, Newspaper, FileText, Layers } from "lucide-react";
import { Dialog, DialogContent } from "./ui/dialog";
import { Input } from "./ui/input";
import { categories, news } from "../mock";

const PAGES = [
  { type: "page", label: "Home", to: "/", desc: "Ruken Alazdehar — landing page" },
  { type: "page", label: "About Us", to: "/about-us", desc: "Company overview, vision and mission" },
  { type: "page", label: "Product Categories", to: "/product-categories", desc: "Browse all 6 product categories" },
  { type: "page", label: "News & Insights", to: "/news", desc: "Latest company news and highlights" },
  { type: "page", label: "Contact Us", to: "/contact-us", desc: "Office, phone, email and inquiry form" },
  { type: "page", label: "Supplier Center", to: "/supplier-center", desc: "Register as a supplier or manufacturer" },
];

function buildIndex() {
  const items = [];
  PAGES.forEach((p) => items.push({ ...p, icon: FileText }));
  categories.forEach((c) => {
    items.push({
      type: "category",
      label: `Category ${c.code} — ${c.title}`,
      desc: c.short,
      to: `/products/${c.slug}`,
      icon: Layers,
    });
    c.products.forEach((p) => {
      items.push({
        type: "product",
        label: p.name,
        desc: `${c.title} · ${p.spec || ""}`.trim(),
        to: `/products/${c.slug}#${encodeURIComponent(p.name)}`,
        icon: Package,
      });
    });
  });
  news.forEach((n) => {
    items.push({
      type: "news",
      label: n.title,
      desc: new Date(n.date).toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" }),
      to: `/news/${n.slug}`,
      icon: Newspaper,
    });
  });
  return items;
}

export default function SearchDialog({ open, onOpenChange }) {
  const [q, setQ] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const navigate = useNavigate();
  const items = useMemo(() => buildIndex(), []);

  const results = useMemo(() => {
    const v = q.trim().toLowerCase();
    if (!v) return items.slice(0, 8);
    return items
      .filter(
        (it) =>
          it.label.toLowerCase().includes(v) ||
          (it.desc || "").toLowerCase().includes(v) ||
          it.type.toLowerCase().includes(v)
      )
      .slice(0, 20);
  }, [q, items]);

  useEffect(() => {
    setActiveIdx(0);
  }, [q, open]);

  useEffect(() => {
    if (!open) setQ("");
  }, [open]);

  // Cmd+K / Ctrl+K to open
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        onOpenChange(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onOpenChange]);

  const onKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(results.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const it = results[activeIdx];
      if (it) {
        navigate(it.to);
        onOpenChange(false);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
          <Search size={18} className="text-muted-foreground" />
          <Input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKey}
            placeholder="Search products, categories, news..."
            className="border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 px-0 h-9 text-base"
          />
          <button onClick={() => onOpenChange(false)} className="text-muted-foreground hover:text-foreground" aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto py-2">
          {results.length === 0 ? (
            <div className="px-6 py-10 text-center text-muted-foreground text-sm">
              No results found for <span className="text-foreground font-medium">"{q}"</span>
            </div>
          ) : (
            results.map((it, i) => {
              const Icon = it.icon || Package;
              const isActive = i === activeIdx;
              return (
                <button
                  key={`${it.to}-${i}`}
                  onClick={() => {
                    navigate(it.to);
                    onOpenChange(false);
                  }}
                  onMouseEnter={() => setActiveIdx(i)}
                  className={`w-full flex items-start gap-3 text-left px-5 py-3 transition ${
                    isActive ? "bg-muted/60" : "hover:bg-muted/40"
                  }`}
                >
                  <span
                    className="w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0"
                    style={{ background: "var(--brand-100)" }}
                  >
                    <Icon size={16} style={{ color: "var(--brand-700)" }} />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="flex items-center gap-2">
                      <span className="font-medium text-foreground truncate">{it.label}</span>
                      <span
                        className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded"
                        style={{ background: "var(--brand-50)", color: "var(--brand-700)" }}
                      >
                        {it.type}
                      </span>
                    </span>
                    {it.desc && <span className="block text-xs text-muted-foreground mt-0.5 truncate">{it.desc}</span>}
                  </span>
                </button>
              );
            })
          )}
        </div>
        <div className="px-5 py-3 border-t border-border flex items-center justify-between text-[11px] text-muted-foreground bg-muted/30">
          <span className="flex items-center gap-3">
            <kbd className="px-1.5 py-0.5 rounded border border-border bg-white">↑↓</kbd> navigate
            <kbd className="px-1.5 py-0.5 rounded border border-border bg-white">↵</kbd> select
            <kbd className="px-1.5 py-0.5 rounded border border-border bg-white">Esc</kbd> close
          </span>
          <span>Search by Ruken Alazdehar</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
